import { useEffect, useRef, useState } from "react";

import { Tabs } from "antd";
import { cx } from "class-variance-authority";

import * as theme from "./Anchors.css";
import { useApplicationBarVariant } from "./ApplicationBarVariant";

const INITIAL_SCROLL_DELAY = 500;
const ANIMATION_FINISHED_MATCH_FRAMES = 5;
// Fine-grained, so a section's top leaving the visible content is noticed within
// 1% of the section's height
const VISIBILITY_THRESHOLDS = Array.from(
    { length: 101 },
    (_, step) => step / 100
);

export interface Anchor {
    elementId: string;
    label: string;
}
export interface AnchorsProps {
    anchors: Anchor[];
    /**
     * Called when an anchor's section is not on the page.
     */
    onError?: (error: Error) => void;
}
export function Anchors({ anchors, onError = noop }: AnchorsProps) {
    const variant = useApplicationBarVariant();
    const isAnimating = useRef<boolean>(false);

    const [currentAnchor, setCurrentAnchor] = useState<string | undefined>();
    // Ensures currentAnchor is initialized
    useEffect(() => {
        if (currentAnchor) {
            return;
        }
        const elementId = window.location.hash.substring(1);
        if (!elementId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setCurrentAnchor(anchors[0].elementId);
            return;
        }
        isAnimating.current = true;
        setTimeout(() => {
            smoothScroll(elementId, onError).then(() => {
                isAnimating.current = false;
            });
            setCurrentAnchor(elementId);
        }, INITIAL_SCROLL_DELAY);
    }, [anchors, currentAnchor, onError]);

    // Highlights the first section whose top edge is in view as the content scrolls
    useEffect(
        () =>
            observeFirstSectionWithVisibleTop(anchors, onError, (elementId) => {
                if (isAnimating.current) {
                    // User triggered scroll to section - don't update
                    return;
                }
                setCurrentAnchor(elementId);
            }),
        [anchors, onError]
    );

    const handleClick = (elementId: string) => {
        isAnimating.current = true;
        // replaceState replaces the current history entry - this ensures e.g. programmatic back navigation works as the user expects
        history.replaceState(null, "", `#${elementId}`);

        setCurrentAnchor(elementId);
        smoothScroll(elementId, onError).then(() => {
            isAnimating.current = false;
        });
    };

    const tabs = anchors.map((anchor) => {
        const labelClasses = cx(
            theme.anchor[variant],
            anchor.elementId === currentAnchor && theme.anchorActive
        );
        return {
            label: <a className={labelClasses}>{anchor.label}</a>,
            key: anchor.elementId,
        };
    });

    return (
        <Tabs
            onChange={handleClick}
            activeKey={currentAnchor}
            items={tabs}
            className={theme.tabs[variant]}
        />
    );
}

function noop() {}

const smoothScroll = (elementId: string, onError: (error: Error) => void) =>
    new Promise<void>((resolve) => {
        const element = document.getElementById(elementId);

        if (!element) {
            const error = new Error(
                `Expected element ${elementId} to be defined!`
            );
            console.error(error);
            onError(error);
            return;
        }

        const scrollIntoView = () =>
            element.scrollIntoView({ behavior: "smooth" });

        const checkAnimationFinished = (
            matchCount: number,
            prevRectTop: number | null
        ) => {
            const rectTop = element.getBoundingClientRect().top;

            if (rectTop === prevRectTop) {
                if (matchCount + 1 > ANIMATION_FINISHED_MATCH_FRAMES) {
                    resolve();
                } else {
                    matchCount += 1;
                }
            } else {
                matchCount = 0;
                prevRectTop = rectTop;
            }

            requestAnimationFrame(() =>
                checkAnimationFinished(matchCount, prevRectTop)
            );
        };

        scrollIntoView();
        checkAnimationFinished(0, null);
    });

const observeFirstSectionWithVisibleTop = (
    anchors: Anchor[],
    onError: (error: Error) => void,
    onChange: (elementId: string) => void
) => {
    const topVisibility = new Map<string, boolean>();
    const observer = new IntersectionObserver(
        (entries) => {
            // Each section's first entry comes from observe(), not a scroll,
            // and would override an anchor picked by a click or the URL hash
            const isScroll = entries.some((entry) =>
                topVisibility.has(entry.target.id)
            );
            entries.forEach((entry) =>
                topVisibility.set(entry.target.id, isTopVisible(entry))
            );
            const firstWithVisibleTop = anchors.find((anchor) =>
                topVisibility.get(anchor.elementId)
            );
            if (isScroll && firstWithVisibleTop) {
                onChange(firstWithVisibleTop.elementId);
            }
        },
        { threshold: VISIBILITY_THRESHOLDS }
    );
    getSectionElements(anchors, onError).forEach((element) =>
        observer.observe(element)
    );
    return () => observer.disconnect();
};

// The scrolling content clips the intersection, so a clipped top means the
// section's top has scrolled out of view
const isTopVisible = (entry: IntersectionObserverEntry) =>
    entry.isIntersecting &&
    entry.intersectionRect.top - entry.boundingClientRect.top < 1;

const getSectionElements = (
    anchors: Anchor[],
    onError: (error: Error) => void
) =>
    anchors.flatMap((anchor) => {
        const element = document.getElementById(anchor.elementId);
        if (!element) {
            const error = new Error(
                `Expected element ${anchor.elementId} to be defined!`
            );
            console.error(error);
            onError(error);
            return [];
        }
        return [element];
    });
